import React, { useMemo } from 'react';
import { useSelector } from "react-redux";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, Legend, ResponsiveContainer, LabelList } from 'recharts';
import { processedBudgetedTrackedByProject } from './Services/reportTimeWorked';

const BudgetedTracked = ({ trackReport, trackReportLoading, trackReportError }) => {
  const reportingCliTimeWorked = useSelector((state) => state.reportingCliTimeWorked);

  const processedData = useMemo(() => {
    return processedBudgetedTrackedByProject(
      trackReport,
      reportingCliTimeWorked,
    );
  }, [trackReport, reportingCliTimeWorked,]);

  const calculateChartHeight = (dataLength) => {
    const rowHeight = 50;
    const baseHeight = 100;
    return dataLength * rowHeight + baseHeight;
  };

  if (trackReportLoading) {
    const skeletonRows = 8;
    const rowHeight = 50;
    const baseHeight = 100;
    const skeletonHeight = skeletonRows * rowHeight + baseHeight;
    
    // Fixed widths for skeleton bars to avoid random values
    const barWidths = [45, 60, 35, 55, 40, 50, 38, 48];
    
    return (
      <div className='graph-project-per-hour' style={{ width: '100%', height: Math.max(542, skeletonHeight) }}>
        <div style={{ 
          width: '100%', 
          height: '100%', 
          padding: '20px 42px 5px 0px',
          position: 'relative'
        }}>
          {/* Y-axis skeleton labels */}
          <div style={{ 
            position: 'absolute', 
            left: '0', 
            top: '20px', 
            bottom: '5px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            paddingRight: '10px',
            width: '150px'
          }}>
            {Array.from({ length: skeletonRows }).map((_, index) => (
              <div
                key={index}
                style={{
                  width: '120px',
                  height: '13px',
                  backgroundColor: '#e5e7eb',
                  borderRadius: '4px',
                  animation: 'pulse 1.5s ease-in-out infinite',
                }}
              />
            ))}
          </div>

          {/* Chart area with bars */}
          <div style={{ 
            marginLeft: '150px',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            paddingTop: '10px',
            paddingBottom: '30px',
            position: 'relative'
          }}>
            {/* Vertical grid lines skeleton */}
            <div style={{ 
              position: 'absolute',
              top: '10px',
              left: '0',
              right: '42px',
              bottom: '30px',
              display: 'flex',
              justifyContent: 'space-between'
            }}>
              {Array.from({ length: 6 }).map((_, index) => (
                <div
                  key={index}
                  style={{
                    width: '1px',
                    height: '100%',
                    background: 'repeating-linear-gradient(to bottom, #D6D9DB 0px, #D6D9DB 3px, transparent 3px, transparent 6px)',
                    opacity: 0.3
                  }}
                />
              ))}
            </div>

            {/* Two bars per row skeleton - positioned side by side horizontally */}
            {Array.from({ length: skeletonRows }).map((_, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  height: '50px',
                  position: 'relative',
                  gap: '4px'
                }}
              >
                {/* Budgeted bar skeleton - starts from left */}
                <div
                  style={{
                    width: `${barWidths[index] || 45}%`,
                    height: '13px',
                    backgroundColor: '#87ABE2',
                    opacity: 0.3,
                    borderRadius: '2px',
                    animation: 'pulse 1.5s ease-in-out infinite',
                    minWidth: '60px'
                  }}
                />
                {/* Tracked bar skeleton - positioned right next to budgeted */}
                <div
                  style={{
                    width: `${(barWidths[index] || 45) - 10}%`,
                    height: '13px',
                    backgroundColor: '#C7B6F6',
                    opacity: 0.3,
                    borderRadius: '2px',
                    animation: 'pulse 1.5s ease-in-out infinite',
                    animationDelay: '0.2s',
                    minWidth: '50px'
                  }}
                />
              </div>
            ))}
          </div>

          {/* X-axis skeleton ticks */}
          <div style={{ 
            position: 'absolute', 
            bottom: '5px', 
            left: '150px', 
            right: '42px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end'
          }}>
            {Array.from({ length: 6 }).map((_, index) => (
              <div
                key={index}
                style={{
                  width: '40px',
                  height: '12px',
                  backgroundColor: '#e5e7eb',
                  borderRadius: '4px',
                  animation: 'pulse 1.5s ease-in-out infinite',
                }}
              />
            ))}
          </div>

          {/* Legend skeleton - positioned at top right */}
          <div style={{ 
            position: 'absolute',
            top: '0',
            right: '42px',
            display: 'flex',
            gap: '20px',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '13px',
                height: '13px',
                backgroundColor: '#87ABE2',
                borderRadius: '2px',
                opacity: 0.3,
                animation: 'pulse 1.5s ease-in-out infinite',
              }} />
              <div style={{
                width: '100px',
                height: '12px',
                backgroundColor: '#e5e7eb',
                borderRadius: '4px',
                animation: 'pulse 1.5s ease-in-out infinite',
              }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '13px',
                height: '13px',
                backgroundColor: '#C7B6F6',
                borderRadius: '2px',
                opacity: 0.3,
                animation: 'pulse 1.5s ease-in-out infinite',
              }} />
              <div style={{
                width: '100px',
                height: '12px',
                backgroundColor: '#e5e7eb',
                borderRadius: '4px',
                animation: 'pulse 1.5s ease-in-out infinite',
              }} />
            </div>
          </div>
        </div>
        <style>{`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
        `}</style>
      </div>
    );
  }

  if (trackReportError) return <div>Error loading data.</div>;

  const calculateXTicks = () => {
    if (!processedData || !processedData.length) return [0];
    
    const maxDataValue = Math.max(
      ...processedData.map(item => {
        const budgeted = item?.budgeted || 0;
        const tracked = item?.tracked || 0;
        return Math.max(budgeted, tracked);
      })
    );
    
    if (maxDataValue === 0 || isNaN(maxDataValue)) return [0];
    
    const tickInterval = Math.ceil(maxDataValue / 10) || 1;
    const numOfTicks = Math.ceil(maxDataValue / tickInterval);
    const ticks = [];

    for (let i = 0; i <= numOfTicks + 4; i++) {
      ticks.push(i * tickInterval);
    }

    return ticks;
  }

  const xAxisTicks = calculateXTicks();

  const changeDurationFormat = (sec) => {
    const hours = Math.floor(sec / 3600);
    const minutes = Math.floor((sec % 3600) / 60);
    const seconds = sec % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const calculateLeftMargin = (data) => {
    if (!data || data.length === 0) return -20;
    
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    context.font = '12px Arial';

    const longestWordLength = data.reduce((maxLength, item) => {
      if (!item || !item.name) return maxLength;
      const firstWord = item.name.split(' ')[0];
      const wordWidth = context.measureText(firstWord).width;
      return Math.max(maxLength, wordWidth);
    }, 0);

    return Math.max(-20, longestWordLength - 150);
  };

  if (!processedData || processedData.length === 0) {
    return <div style={{ padding: '20px', textAlign: 'center', color: '#6a7988' }}>No data available</div>;
  }

  const leftMargin = calculateLeftMargin(processedData);

  return (
    <div className='graph-project-per-hour' style={{ width: '100%', height: Math.max(542, calculateChartHeight(processedData.length)), }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          layout="vertical"
          data={processedData}
          barSize={13}
          margin={{
            top: 20,
            right: 42,
            left: leftMargin,
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={false} vertical={true} />
          <XAxis
            type="number"
            domain={[0, 'dataMax + 10']}
            tickFormatter={(tick) => changeDurationFormat(tick)}
            ticks={xAxisTicks}
          />
          <YAxis
            type="category"
            dataKey="name"
            width={150}
            tickMargin={10}
          />
          <Tooltip formatter={(value) => changeDurationFormat(value)} />
          <Legend />
          <ReferenceLine x={0} stroke="#D3D3D3" />
          <Bar dataKey="budgeted" name="Budgeted hours" fill="#87ABE2">
            <LabelList dataKey="budgeted" position="right" formatter={(value) => value !== 0 ? changeDurationFormat(value) : null} />
          </Bar>
          <Bar dataKey="tracked" name="Tracked Hours" fill="#C7B6F6">
            <LabelList dataKey="tracked" position="right" formatter={(value) => value !== 0 ? changeDurationFormat(value) : null} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default BudgetedTracked;
